import { clamp, mean, stdDev } from './statistics'

export interface DrawdownResult {
  maxDrawdown: number
  maxDrawdownPct: number
  peak: number
  trough: number
  longestDrawdownLength: number
  currentDrawdownPct: number
}

export function drawdown(equityCurve: readonly number[]): DrawdownResult {
  if (equityCurve.length === 0) {
    return {
      maxDrawdown: 0,
      maxDrawdownPct: 0,
      peak: 0,
      trough: 0,
      longestDrawdownLength: 0,
      currentDrawdownPct: 0,
    }
  }
  let peak = equityCurve[0]
  let maxDd = 0
  let maxDdPct = 0
  let peakAt = 0
  let troughAt = 0
  let longest = 0
  let currentLen = 0

  for (let i = 0; i < equityCurve.length; i++) {
    const v = equityCurve[i]
    if (v > peak) {
      peak = v
      currentLen = 0
    } else {
      currentLen++
      if (currentLen > longest) longest = currentLen
    }
    const dd = peak - v
    const ddPct = peak > 0 ? dd / peak : 0
    if (dd > maxDd) {
      maxDd = dd
      maxDdPct = ddPct
      peakAt = peak
      troughAt = v
    }
  }

  const last = equityCurve[equityCurve.length - 1]
  const runningPeak = Math.max(...equityCurve)
  const currentDdPct = runningPeak > 0 ? (runningPeak - last) / runningPeak : 0

  return {
    maxDrawdown: maxDd,
    maxDrawdownPct: maxDdPct,
    peak: peakAt,
    trough: troughAt,
    longestDrawdownLength: longest,
    currentDrawdownPct: currentDdPct,
  }
}

export function longestLosingStreak(results: readonly number[]): number {
  let longest = 0
  let current = 0
  for (const r of results) {
    if (r < 0) {
      current++
      if (current > longest) longest = current
    } else {
      current = 0
    }
  }
  return longest
}

export interface Mulberry32 {
  (): number
}

export function mulberry32(seed: number): Mulberry32 {
  let a = seed >>> 0
  return function next(): number {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export interface RiskOfRuinInputs {
  bankroll: number
  winProb: number
  stakeFraction: number
  payoutMultiple: number
  bets: number
  ruinThresholdPct?: number
  simulations?: number
  seed?: number
}

export interface RiskOfRuinResult {
  riskOfRuinPct: number
  medianEnding: number
  meanEnding: number
  p5Ending: number
  p95Ending: number
  worstEnding: number
  bestEnding: number
  avgMaxDrawdownPct: number
}

export function riskOfRuin(inputs: RiskOfRuinInputs): RiskOfRuinResult {
  const sims = inputs.simulations ?? 2000
  const ruinThreshold = inputs.bankroll * (inputs.ruinThresholdPct ?? 0.0)
  const rng = mulberry32(inputs.seed ?? 1337)
  const endings: number[] = []
  const drawdowns: number[] = []
  let ruined = 0

  for (let s = 0; s < sims; s++) {
    let balance = inputs.bankroll
    let peak = balance
    let maxDdPct = 0
    let isRuined = false
    for (let i = 0; i < inputs.bets; i++) {
      const stake = balance * inputs.stakeFraction
      if (rng() < inputs.winProb) balance += stake * (inputs.payoutMultiple - 1)
      else balance -= stake
      if (balance > peak) peak = balance
      const ddPct = peak > 0 ? (peak - balance) / peak : 0
      if (ddPct > maxDdPct) maxDdPct = ddPct
      if (balance <= ruinThreshold || balance <= 0) {
        isRuined = true
        balance = Math.max(balance, 0)
        break
      }
    }
    if (isRuined) ruined++
    endings.push(balance)
    drawdowns.push(maxDdPct)
  }

  endings.sort((a, b) => a - b)
  const pct = (q: number) => endings[clamp(Math.floor(q * (endings.length - 1)), 0, endings.length - 1)]

  return {
    riskOfRuinPct: (ruined / sims) * 100,
    medianEnding: pct(0.5),
    meanEnding: mean(endings),
    p5Ending: pct(0.05),
    p95Ending: pct(0.95),
    worstEnding: endings[0],
    bestEnding: endings[endings.length - 1],
    avgMaxDrawdownPct: mean(drawdowns) * 100,
  }
}

export interface MonteCarloInputs {
  bankroll: number
  perBetReturns: readonly number[]
  bets: number
  stakeFraction: number
  simulations?: number
  seed?: number
}

export interface MonteCarloResult {
  paths: number[][]
  endings: number[]
  medianEnding: number
  p5Ending: number
  p95Ending: number
  probLossPct: number
  avgMaxDrawdownPct: number
}

export function monteCarloBankroll(inputs: MonteCarloInputs): MonteCarloResult {
  const sims = inputs.simulations ?? 500
  const rng = mulberry32(inputs.seed ?? 7)
  const samples = inputs.perBetReturns
  const paths: number[][] = []
  const endings: number[] = []
  const drawdowns: number[] = []
  const keepPaths = Math.min(sims, 40)

  if (samples.length === 0) {
    return {
      paths: [],
      endings: [],
      medianEnding: inputs.bankroll,
      p5Ending: inputs.bankroll,
      p95Ending: inputs.bankroll,
      probLossPct: 0,
      avgMaxDrawdownPct: 0,
    }
  }

  for (let s = 0; s < sims; s++) {
    let balance = inputs.bankroll
    let peak = balance
    let maxDdPct = 0
    const path: number[] = [balance]
    for (let i = 0; i < inputs.bets; i++) {
      const r = samples[Math.floor(rng() * samples.length)]
      balance += balance * inputs.stakeFraction * r
      balance = Math.max(balance, 0)
      if (balance > peak) peak = balance
      const ddPct = peak > 0 ? (peak - balance) / peak : 0
      if (ddPct > maxDdPct) maxDdPct = ddPct
      if (s < keepPaths) path.push(balance)
    }
    if (s < keepPaths) paths.push(path)
    endings.push(balance)
    drawdowns.push(maxDdPct)
  }

  endings.sort((a, b) => a - b)
  const pct = (q: number) => endings[clamp(Math.floor(q * (endings.length - 1)), 0, endings.length - 1)]
  const losses = endings.filter((e) => e < inputs.bankroll).length

  return {
    paths,
    endings,
    medianEnding: pct(0.5),
    p5Ending: pct(0.05),
    p95Ending: pct(0.95),
    probLossPct: (losses / endings.length) * 100,
    avgMaxDrawdownPct: mean(drawdowns) * 100,
  }
}

export function sharpeLike(returns: readonly number[], periodsPerYear = 252): number {
  if (returns.length < 2) return 0
  const m = mean(returns)
  const s = stdDev(returns)
  if (s === 0) return 0
  return (m / s) * Math.sqrt(periodsPerYear)
}
